import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography
} from '@material-ui/core';
import { useSnackbar } from '../../../../../hooks/useSnackbar';
import { deleteUserSavedView } from '../../../../../services/savedView';
import { ButtonWithPromise } from '../../../../../components/ButtonWithPromise';
import { DialogTitleWithTypography } from '../../../../../components/DialogTitleWithTypography';
import { InfoBox } from '../../../../../components/InfoBox';

type Props = {
  open: boolean;
  onClose: () => void;
  viewId: string;
  viewName: string;
  /**
   * Creator vs shared-with member. Drives copy and outcome:
   *   - creator     → the whole view is deleted (for everyone).
   *   - shared-with → only the current user's access is removed.
   */
  isCreator: boolean;
  /** How many users the view is shared with (creator case). */
  sharedWithCount: number;
  /** Display name of the view's creator (shared-with-me case). */
  creatorName: string;
  /** Called after a successful delete / access-removal. */
  onConfirmed: () => void;
  /** Called when the backend reports the view is gone / access changed. */
  onStale?: () => void;
};

export const DeleteViewModal = ({
  open,
  onClose,
  viewId,
  viewName,
  isCreator,
  sharedWithCount,
  creatorName,
  onConfirmed,
  onStale
}: Props) => {
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();

  const handleConfirm = async () => {
    try {
      const { action } = await deleteUserSavedView(viewId);
      enqueueSnackbarSuccess(
        action === 'deleted'
          ? 'Saved view deleted.'
          : 'You no longer have access to this saved view.'
      );
      onConfirmed();
    } catch (err: any) {
      if (err?.code === 'permission-denied' || err?.code === 'not-found') {
        enqueueSnackbarError(
          err?.message ||
            'This saved view is no longer available. Refreshing your list.'
        );
        onStale?.();
        return;
      }
      enqueueSnackbarError(
        err?.message ||
          (isCreator
            ? 'Failed to delete view. Please try again.'
            : 'Failed to remove access. Please try again.')
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        Delete view: {viewName}
      </DialogTitleWithTypography>
      <DialogContent>
        <InfoBox color="red">
          <Typography variant="body1">
            {!isCreator ? (
              <>
                This view was shared with you by <strong>{creatorName}</strong>.
                Clicking delete will remove your access to the view, but
                won&apos;t delete it for anyone else.
              </>
            ) : sharedWithCount > 0 ? (
              <>
                This view is shared with{' '}
                <strong>
                  {sharedWithCount} {sharedWithCount === 1 ? 'person' : 'people'}
                </strong>
                . They&apos;ll lose access to it too. This can&apos;t be undone.
              </>
            ) : (
              <>Deleting this view can&apos;t be undone.</>
            )}
          </Typography>
        </InfoBox>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <ButtonWithPromise
          variant="contained"
          color="secondary"
          onClick={handleConfirm}
          pending="Deleting..."
        >
          Delete view
        </ButtonWithPromise>
      </DialogActions>
    </Dialog>
  );
};
